/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import React from 'react';

import { Icon, Label, Step, StepGroup } from '@mairistem/composants';

import { type AssistantStep, Steps } from '@/types';
import { COLORS } from '@/constants';
import { AssistantContext } from '@/contexts';

interface Props {
  /**
   * Index de l'étape actuelle
   * 0 : Identification
   * 1 : Adresses & contacts
   * 2 : Filiation
   * 3 : Informations complémentaires
   */
  currentStepIndex: number;
  /** Toutes les étapes de l'assistant */
  steps: Partial<Record<Steps, AssistantStep>>;
  /** Callback appelé lors du changement d'étape */
  onStepChange: (stepIndex: Steps) => void;
  /** Mode Hubee actif */
  isHubeeMode?: boolean;
}

const AssistantHeader = (props: Props) => {
  // #region Declarations
  const { currentStepIndex, steps, onStepChange, isHubeeMode = false } = props;

  const individu = React.useContext(AssistantContext);
  const { errors } = individu || {};

  // #endregion

  // #region Component

  return (
    <StepGroup
      fluid
      className="connected"
      style={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}
    >
      {_.map(steps, (step: AssistantStep, index: Steps) => {
        const { title, dataTestId, validated = false } = step;

        const stepIndex: Steps = Number(index);
        const errorsStep = errors?.[stepIndex];

        // En mode Hubee, vérifier si on peut accéder à la partie 5 (Attestation)
        let isDisabled = false;
        if (isHubeeMode && stepIndex === Steps.Attestation) {
          // Vérifier si les 4 premières étapes ont des erreurs
          const hasErrorsInPreviousSteps = [
            Steps.Identification,
            Steps.AdressesContacts,
            Steps.Filiation,
            Steps.InformationsComplementaires,
          ].some((stepToCheck) => !_.isEmpty(errors?.[stepToCheck]));

          isDisabled = hasErrorsInPreviousSteps;
        }

        // TODO : lors de la gestion des erreurs, adapter cette partie de code
        // if (!_.isEmpty(errorsStep)) {
        //   content = (
        //     <Icon
        //       fitted
        //       color="red"
        //       size="big"
        //       name="times circle"
        //     />
        //   );
        // }

        const content: React.ReactNode = (
          <Label
            circular
            size="big"
            style={{
              backgroundColor:
                stepIndex <= currentStepIndex ? COLORS.Background : 'white',
              border: `solid 1px ${COLORS.HeaderStepBorder}`,
              color:
                stepIndex <= currentStepIndex ? 'white' : COLORS.Background,
            }}
          >
            {stepIndex + 1}
            {(validated || stepIndex < currentStepIndex) &&
              _.isEmpty(errorsStep) && (
                <Icon
                  name="checkmark"
                  style={{
                    position: 'absolute',
                    top: '40%',
                    left: '55%',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    color: 'green',
                    width: 17,
                    height: 20,
                  }}
                />
              )}
            {!_.isEmpty(errorsStep) && stepIndex < currentStepIndex && (
              <Icon
                name="close"
                style={{
                  position: 'absolute',
                  top: '40%',
                  left: '53%',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  color: 'red',
                  width: 17,
                  height: 20,
                }}
              />
            )}
          </Label>
        );

        return (
          <Step
            key={title}
            style={{
              backgroundColor: 'transparent',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
            }}
            data-testid={dataTestId}
            onClick={() => !isDisabled && onStepChange(stepIndex)}
          >
            <Step.Content style={{ bottom: '0.6em' }}>
              {content}
              <Step.Title style={{ paddingTop: 5, color: COLORS.Background }}>
                {title}
              </Step.Title>
            </Step.Content>
          </Step>
        );
      })}
    </StepGroup>
  );
};

export default AssistantHeader;
